'use client';
import { AdminPanelSettings, Apps, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Badge,
  Box,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import LoadingIndicator from '@/components/shared/loading-indicator';
import ThemePickerDialog from '@/components/shared/theme-picker-dialog';
import { mainMenuItems } from '@/constants/menu';
import { teamMemberRoles } from '@/features/manage/constants/navigation';
import { useAnnouncements, useMyAnnouncements } from '@/hooks';
import { useAuth, useCourses } from '@/providers';
import { useThemeStore, useViewStore } from '@/stores';
import type { MenuItem } from '@/types';
import { useRoute } from '@/utils/client/route';
import { getColorModeIcon, getColorModeLabel } from '@/utils/theme-mode';

const sxBtn = {
  background: 'transparent',
  border: 0,
  color: 'inherit',
  cursor: 'pointer',
};

export default function MainMenu() {
  const { goToRoute } = useRoute();
  const { courses } = useCourses();
  const { toggleMenuDrawer, toggleAnnouncementsModal } = useViewStore();
  const { isLoggedIn, logout, currentUser } = useAuth();
  const { colorMode } = useThemeStore();
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [courseLinks, setCourseLinks] = useState<MenuItem[]>([]);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);

  const staffLinks = useMemo((): MenuItem[] => {
    if (!currentUser || !(teamMemberRoles as readonly string[]).includes(currentUser.role)) {
      return [];
    }

    return [
      {
        icon: <AdminPanelSettings />,
        title: 'Admin Panel',
        routeId: 'manageDashboard',
      },
    ];
  }, [currentUser]);

  // Get user announcements to check for badge (logged-in users)
  const { data: myAnnouncements = [] } = useMyAnnouncements();

  // Get all announcements for non-logged in users
  const { data: allAnnouncements = [] } = useAnnouncements();

  // Check if we should show any badge
  const shouldShowBadge = isLoggedIn ? myAnnouncements.length > 0 : allAnnouncements.length > 0;

  const onNavigateTo = (menuItem: MenuItem) => {
    const { url, routeId, routeParams, action } = menuItem ?? {
      url: undefined,
      routeId: undefined,
      routeParams: undefined,
      action: undefined,
    };

    // Handle custom actions
    if (action) {
      if (action === 'showAnnouncements') {
        toggleAnnouncementsModal('all');
        toggleMenuDrawer();
      }
      return;
    }

    // If full URL, open in new tab
    if (url) {
      if (url.indexOf('/mein-konto/abmelden/') > -1) {
        logout();
      }
      window.open(url, '_blank', 'noreferrer');
      return;
    }
    // Route
    if (routeId) {
      toggleMenuDrawer();
      goToRoute(routeId, routeParams);
    }
  };

  const onToggle = (item: string) => {
    if (openItems.includes(item)) {
      setOpenItems(openItems.filter((i) => i !== item));
    } else {
      setOpenItems([...openItems, item]);
    }
  };

  const renderList = (items: MenuItem[], isOpen: boolean) => (
    <Collapse in={isOpen} timeout="auto" unmountOnExit>
      {isEmpty(items) ? (
        <Box sx={{ ml: 2 }}>
          <LoadingIndicator type="inline" label="Menü wird geladen..." />
        </Box>
      ) : (
        <List component="div" disablePadding>
          {items.map((item) => (
            <ListItem
              key={item.url ?? item.title}
              component="button"
              sx={{ pl: 4, ...sxBtn }}
              onClick={() => onNavigateTo(item)}>
              {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
              <ListItemText primary={item.title} />
            </ListItem>
          ))}
        </List>
      )}
    </Collapse>
  );

  const renderMenuItem = (menuItem: MenuItem) => {
    const { action, icon, title, children, needsLogin, url } = menuItem;
    const itemKey = url ?? title;

    if (needsLogin && !isLoggedIn) return null;
    const isExpanded = openItems.includes(title);

    if (children) {
      return (
        <Box key={itemKey}>
          <ListItem component="button" sx={sxBtn} onClick={() => onToggle(title)}>
            {icon && <ListItemIcon sx={{ minWidth: 0, pr: 1 }}>{icon}</ListItemIcon>}
            <ListItemText primary={title} />
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          {renderList(children, isExpanded)}
        </Box>
      );
    }

    return (
      <ListItem key={itemKey} component="a" onClick={() => onNavigateTo(menuItem)} sx={sxBtn}>
        {icon && <ListItemIcon sx={{ minWidth: 0, pr: 1 }}>{icon}</ListItemIcon>}
        <ListItemText primary={title} />
        {action === 'showAnnouncements' && shouldShowBadge ? (
          <Badge
            {...(isLoggedIn
              ? { badgeContent: myAnnouncements.length }
              : { variant: 'dot' as const })}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                minWidth: 20,
                height: 20,
                borderRadius: 10,
                mr: '13px',
              },
            }}
          />
        ) : null}
      </ListItem>
    );
  };

  useEffect(() => {
    if (courses.length > 0 && !courseLinks.length) {
      const newCourseLinks: MenuItem[] = [
        {
          icon: <Apps />,
          title: 'Meine Kurse',
          children: courses.map((course) => ({
            title: course.title,
            routeId: 'myCourse',
            routeParams: {
              courseSlug: course.slug,
            },
          })),
        },
      ];
      setCourseLinks(newCourseLinks);
    }
  }, [courseLinks, courses]);

  return (
    <>
      <List component="nav" aria-labelledby="nested-list-subheader" sx={{ width: '100%' }}>
        {staffLinks.map((item) => renderMenuItem(item))}
        {courseLinks.map((item) => renderMenuItem(item))}
        {mainMenuItems.map((item) => renderMenuItem(item))}
        <ListItem component="button" onClick={() => setThemeDialogOpen(true)} sx={sxBtn}>
          <ListItemIcon sx={{ minWidth: 0, pr: 1 }}>{getColorModeIcon(colorMode)}</ListItemIcon>
          <ListItemText primary="Design" />
          <Typography color="textSecondary" variant="body2">
            {getColorModeLabel(colorMode)}
          </Typography>
        </ListItem>
      </List>
      <ThemePickerDialog onClose={() => setThemeDialogOpen(false)} open={themeDialogOpen} />
    </>
  );
}
